'use client'

import { ForesightManager } from 'js.foresight'
import { useCallback, useEffect, useRef } from 'react'

const scheduleIdle = (cb: () => void): number =>
  typeof requestIdleCallback === 'function'
    ? requestIdleCallback(cb)
    : (setTimeout(cb, 1) as unknown as number)

const cancelIdle = (id: number): void => {
  if (typeof cancelIdleCallback === 'function') {
    cancelIdleCallback(id)
  } else {
    clearTimeout(id)
  }
}

interface UseForesightOptions {
  callback: () => void
  hitSlop?: number
  reactivateAfter?: number
  enabled?: boolean
}

export function useForesight({
  callback,
  hitSlop,
  reactivateAfter,
  enabled = true,
}: UseForesightOptions) {
  const elementRef = useRef<HTMLAnchorElement>(null)
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const stableCallback = useCallback(() => {
    callbackRef.current()
  }, [])

  useEffect(() => {
    if (!enabled || !elementRef.current) {
      return
    }

    // disable foresight on mobile devices to improve performance on initial load
    if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
      return
    }

    // ForesightManager.register reads getBoundingClientRect synchronously.
    // Defer it off the mount/hydration commit (idle time) to avoid a forced
    // reflow on the load critical path — prefetch is speculative and not urgent.
    let result:
      | ReturnType<typeof ForesightManager.instance.register>
      | undefined
    const idleId = scheduleIdle(() => {
      if (!elementRef.current) {
        return
      }
      result = ForesightManager.instance.register({
        element: elementRef.current,
        callback: stableCallback,
        hitSlop,
        reactivateAfter,
      })
    })

    return () => {
      cancelIdle(idleId)
      result?.unregister()
    }
  }, [stableCallback, hitSlop, reactivateAfter, enabled])

  return { elementRef }
}
